import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import AsyncSelect from 'react-select/async'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { createFilter } from 'react-select'
import { Plus } from 'lucide-react'
import { toast } from 'sonner'
import { useOrganization } from '../contexts/OrganizationContext'
import { useCanManageCreators } from '../hooks/useCanManageCreators'
import { creatorApi } from '../services/api'
import { REACT_SELECT_STYLES } from './common/reactSelectStyles'
import CreatorCreateDialog from './CreatorCreateDialog'
import styles from './CreatorPicker.module.css'

function creatorUnavailable(creator, offeringId) {
  const allowedOfferings = creator?.allowed_offerings || []
  return Boolean(
    offeringId
    && allowedOfferings.length
    && !allowedOfferings.includes(offeringId),
  )
}

function creatorOptions(data, offeringId) {
  return (data?.creators || []).map((creator) => ({
    value: creator.id,
    label: creator.display_name || creator.id,
    creator,
    unavailable: creatorUnavailable(creator, offeringId),
  }))
}

const CREATOR_SCOPE = 'targets'
const EMPTY_IDS = []
const ADD_CREATOR_OPTION = { value: '__add-creator__', label: 'Add creator', action: 'create' }
const baseFilterOption = createFilter()
// Server-side search already filters creator options; the action row must
// survive any typed query, which the default label filter would drop.
const filterOption = (candidate, input) => (
  Boolean(candidate.data?.action) || Boolean(candidate.data?.creator) || baseFilterOption(candidate, input)
)

// scope=targets asks the API for creators eligible for every book the post targets.
function targetSearchOptions(additionalOrgIds) {
  return additionalOrgIds.length
    ? { scope: CREATOR_SCOPE, organizationIds: additionalOrgIds }
    : { scope: CREATOR_SCOPE }
}

function CreatorPicker({
  value,
  onChange,
  selectedCreator,
  offeringId = '',
  additionalOrgIds = EMPTY_IDS,
  onResolvedCreator,
  onValidityChange,
  allowCreate = false,
}) {
  const { currentOrg } = useOrganization()
  const canManageCreators = useCanManageCreators()
  const queryClient = useQueryClient()
  const [searchError, setSearchError] = useState(false)
  const [searchInput, setSearchInput] = useState('')
  const [searchResultCount, setSearchResultCount] = useState(null)
  const [clearedNotice, setClearedNotice] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)
  const withCreateOption = useCallback((options) => (
    allowCreate ? [ADD_CREATOR_OPTION, ...options] : options
  ), [allowCreate])
  const inputId = 'post-creator-creator'
  const orgSlug = currentOrg?.slug || ''
  const sortedTargets = useMemo(() => [...additionalOrgIds].sort(), [additionalOrgIds])
  const targetKey = sortedTargets.join(',')
  const searchOptions = useMemo(() => targetSearchOptions(sortedTargets), [sortedTargets])
  const contextKey = `${orgSlug}:${offeringId}:${targetKey}`
  const asyncSearchRef = useRef({ contextKey, latestRequest: 0 })
  if (asyncSearchRef.current.contextKey !== contextKey) {
    asyncSearchRef.current = { contextKey, latestRequest: 0 }
  }
  const selectedCreatorContextRef = useRef({ contextKey, creator: selectedCreator })
  if (selectedCreatorContextRef.current.creator !== selectedCreator) {
    selectedCreatorContextRef.current = { contextKey, creator: selectedCreator }
  }
  const previousContextKeyRef = useRef(contextKey)
  const creatorQueryKey = ['creator-picker-search', orgSlug, targetKey, '']
  const initialCreatorsQuery = useQuery({
    queryKey: creatorQueryKey,
    queryFn: () => creatorApi.search(orgSlug, '', searchOptions),
    enabled: Boolean(orgSlug),
    staleTime: 30_000,
  })

  const loadOptions = useCallback(
    async (inputValue) => {
      if (!orgSlug) return []
      const requestContext = asyncSearchRef.current
      const requestId = ++requestContext.latestRequest
      try {
        const query = inputValue || ''
        const data = await queryClient.fetchQuery({
          queryKey: ['creator-picker-search', orgSlug, targetKey, query],
          queryFn: () => creatorApi.search(orgSlug, query, searchOptions),
          staleTime: 30_000,
        })
        if (
          asyncSearchRef.current !== requestContext
          || requestContext.latestRequest !== requestId
        ) return []
        setSearchError(false)
        const options = creatorOptions(data, offeringId)
        setSearchResultCount(options.length)
        return withCreateOption(options)
      } catch {
        if (
          asyncSearchRef.current !== requestContext
          || requestContext.latestRequest !== requestId
        ) return []
        setSearchError(true)
        setSearchResultCount(null)
        return withCreateOption([])
      }
    },
    [offeringId, orgSlug, queryClient, searchOptions, targetKey, withCreateOption],
  )
  const initialOptions = creatorOptions(initialCreatorsQuery.data, offeringId)
  const initialSelectedCreator = initialOptions.find((option) => option.value === value)?.creator
  const selectedCreatorQuery = useQuery({
    queryKey: ['creator-picker-id', orgSlug, targetKey, value],
    queryFn: () => creatorApi.search(orgSlug, value, { limit: 1, ...searchOptions }),
    enabled: Boolean(orgSlug && value),
    staleTime: 0,
    refetchOnMount: 'always',
  })
  const directlyLoadedCreator = creatorOptions(
    selectedCreatorQuery.data,
    offeringId,
  ).find((option) => option.value === value)?.creator
  const contextualSelectedCreator = (
    selectedCreatorContextRef.current.contextKey === contextKey
    && selectedCreatorContextRef.current.creator?.id === value
  )
    ? selectedCreatorContextRef.current.creator
    : null
  const resolvedCreator = directlyLoadedCreator || initialSelectedCreator || contextualSelectedCreator
  const exactLookupSettled = Boolean(
    value
    && selectedCreatorQuery.isSuccess
    && !selectedCreatorQuery.isFetching,
  )
  const creatorSelectionValid = !value || Boolean(exactLookupSettled && directlyLoadedCreator)
  const exactLookupKey = `${orgSlug}:${targetKey}:${value || ''}`
  const activeExactLookupKeyRef = useRef(exactLookupKey)
  activeExactLookupKeyRef.current = exactLookupKey
  const clearedLookupRef = useRef('')
  if (!value || directlyLoadedCreator) clearedLookupRef.current = ''
  const unresolvedCreatorName = selectedCreatorQuery.isFetching
    ? 'Loading creator…'
    : 'Creator unavailable'

  useEffect(() => {
    if (previousContextKeyRef.current === contextKey) return
    previousContextKeyRef.current = contextKey
    setSearchError(false)
    setClearedNotice(false)
    onResolvedCreator?.(null)
  }, [contextKey, onResolvedCreator])

  useEffect(() => {
    if (activeExactLookupKeyRef.current === exactLookupKey) {
      onValidityChange?.(creatorSelectionValid)
    }
  }, [creatorSelectionValid, exactLookupKey, onValidityChange])

  useEffect(() => {
    if (
      !exactLookupSettled
      || directlyLoadedCreator
      || !value
      || activeExactLookupKeyRef.current !== exactLookupKey
      || clearedLookupRef.current === exactLookupKey
    ) return
    clearedLookupRef.current = exactLookupKey
    selectedCreatorContextRef.current = { contextKey, creator: null }
    onResolvedCreator?.(null)
    setClearedNotice(true)
    onChange('', undefined)
  }, [
    contextKey,
    exactLookupKey,
    directlyLoadedCreator,
    exactLookupSettled,
    onChange,
    onResolvedCreator,
    value,
  ])

  useEffect(() => {
    if (
      activeExactLookupKeyRef.current === exactLookupKey
      && exactLookupSettled
      && directlyLoadedCreator
      && onResolvedCreator
    ) {
      onResolvedCreator(directlyLoadedCreator)
    }
  }, [directlyLoadedCreator, exactLookupKey, exactLookupSettled, onResolvedCreator])

  const formatOptionLabel = (option) => {
    const { creator, unavailable } = option
    if (option.action) {
      return (
        <div className={styles.option}>
          <span className={styles.addIcon} aria-hidden="true"><Plus size={16} /></span>
          <span className={styles.addLabel}>Add creator</span>
        </div>
      )
    }
    if (!creator) return null
    const initials = (creator.display_name || '?')
      .charAt(0)
      .toUpperCase()

    return (
      <div className={styles.option}>
        {creator.avatar_url ? (
          <img src={creator.avatar_url} alt="" className={styles.avatar} />
        ) : (
          <div className={styles.avatarPlaceholder}>{initials}</div>
        )}
        <div className={styles.optionText}>
          <span className={styles.displayName}>
            {creator.display_name || 'Unnamed'}
          </span>
          {unavailable ? (
            <span className={styles.restriction}>Not available for the selected regulation</span>
          ) : null}
        </div>
      </div>
    )
  }

  const handleCreated = useCallback((creator, { avatarUploadFailed, selected = true } = {}) => {
    queryClient.invalidateQueries({ queryKey: ['creator-picker-search', orgSlug] })
    if (selected) {
      setClearedNotice(false)
      selectedCreatorContextRef.current = { contextKey, creator }
      onChange(creator.id, creator)
    }
    if (avatarUploadFailed) toast.error('Creator added but avatar upload failed')
  }, [contextKey, onChange, orgSlug, queryClient])
  return (
    <div className={styles.wrapper}>
      <label className={styles.label} htmlFor={inputId}>Creator</label>
      <AsyncSelect
        key={contextKey}
        inputId={inputId}
        classNamePrefix="creator-select"
        defaultOptions={withCreateOption(initialOptions)}
        loadOptions={loadOptions}
        formatOptionLabel={formatOptionLabel}
        onInputChange={(inputValue, actionMeta) => {
          if (actionMeta.action === 'input-change') {
            setSearchInput(inputValue)
            setSearchResultCount(null)
          }
          return inputValue
        }}
        filterOption={filterOption}
        isOptionDisabled={(option) => option.unavailable}
        onChange={(option) => {
          if (option?.action) {
            setCreateOpen(true)
            return
          }
          setClearedNotice(false)
          selectedCreatorContextRef.current = { contextKey, creator: option?.creator || null }
          onChange(option ? option.value : '', option?.creator)
        }}
        value={
          value
            ? {
                value,
                label: resolvedCreator?.display_name || unresolvedCreatorName,
                creator: resolvedCreator || { id: value, display_name: unresolvedCreatorName },
                unavailable: creatorUnavailable(resolvedCreator, offeringId),
              }
            : null
        }
        placeholder="Search for a creator..."
        isClearable
        isLoading={initialCreatorsQuery.isLoading || Boolean(value && selectedCreatorQuery.isFetching)}
        noOptionsMessage={({ inputValue }) => {
          if (inputValue) return 'No eligible creators match'
          if (sortedTargets.length) return 'No creators are linked to every selected book'
          return 'No creators yet. Ask an organization admin to add one.'
        }}
        styles={REACT_SELECT_STYLES}
      />
      {clearedNotice && !value ? (
        <p className={styles.notice} role="status">
          The selected creator is not available for the books this post targets. Choose another creator.
        </p>
      ) : null}
      {allowCreate && !value && !searchInput.trim() && initialCreatorsQuery.isSuccess && initialOptions.length === 0 ? (
        <p className={styles.notice} role="status">
          No creators yet. Choose Add creator to set up who this post appears from.
        </p>
      ) : null}
      {allowCreate && searchInput.trim() && searchResultCount === 0 && !searchError ? (
        <p className={styles.notice} role="status">
          No eligible creators match
        </p>
      ) : null}
      {(initialCreatorsQuery.isError || selectedCreatorQuery.isError || searchError) ? (
        <div className={styles.error} role="alert">
          <span>Creators could not be loaded.</span>
          <button
            type="button"
            onClick={() => {
              setSearchError(false)
              initialCreatorsQuery.refetch()
              if (value) selectedCreatorQuery.refetch()
            }}
          >
            Retry
          </button>
        </div>
      ) : null}

      {allowCreate ? (
        <CreatorCreateDialog
          open={createOpen}
          onClose={() => setCreateOpen(false)}
          onCreated={handleCreated}
          allowAvatar={canManageCreators}
        />
      ) : null}
    </div>
  )
}

export default CreatorPicker
